import type {
	AnyAction,
	ListenerEffectAPI,
	ThunkDispatch,
} from '@reduxjs/toolkit';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import { chatEntity } from '~entities/chats/chatEntity';
import type { TMessage } from '~entities/messages';
import {
	loadFromSessionStorage,
	saveToSessionStorage,
	ssKeys,
} from '~features/storage';
import { Toaster } from '~features/toaster';
import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import { GTMAnalytics } from '~services/analytics';
import { authApi } from '~services/api/auth';
import { botsApi } from '~services/api/bots';
import {
	dialogsApi,
	messagesAdapter,
	messagesSelector,
} from '~services/api/dialogs';
import { SUGGESTIONS_RESPONSE_TIMEOUT_MS } from '~services/api/dialogs/constants';
import { generationApi } from '~services/api/generation';
import { userApi } from '~services/api/user';
import { apiTags, prepareMediaItem } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';
import {
	eventsInbox,
	EventType,
	isMsgType,
} from '~shared/services/eventsInbox';
import { chatVideoGenerationActions } from '~store/chat-video-generation';
import { messageReceived } from '~store/utils/message-received';
import { updateChat } from '~store/utils/updateChat';
import {
	adaptAudioMessage,
	adaptSextingMessage,
	adaptVideoMessage,
	addEncodedTextFieldToMessage,
	addGifPath,
	formatIMGPaths,
} from '~utils/messages';

import type { AppDispatch, RootState } from '../index';

export const wsEventListener = createListenerMiddleware();

wsEventListener.startListening({
	matcher: authApi.endpoints.authenticate.matchFulfilled,
	effect: (action, listenerApi) => {
		if (typeof window === 'undefined') {
			return;
		}

		listenerApi.unsubscribe();

		eventsInbox.onMsg((msg) => {
			const { id: userId } = (listenerApi.getState() as RootState).auth.info;
			if (isMsgType(msg, EventType.LevelChanged)) {
				const data = msg.payload;
				const tag = data.changes.reason.messageTag;
				const elem = document.getElementById(tag);
				if (elem) {
					elem.dataset.exp = `+${data.changes.experienceChanged}`;
				}
				listenerApi.dispatch(
					userApi.util.updateQueryData('getLevel', { userId }, (draft) => {
						Object.assign(draft ?? {}, data.current);
					}) as AppDispatch
				);
			}

			const isNewMessage = isMsgType(msg, EventType.ChatMessageReceived);

			if (isNewMessage || isMsgType(msg, EventType.ChatMessageUpdated)) {
				const data = msg.payload;
				const encodedMessage = addEncodedTextFieldToMessage(data);
				const { recipient, sender, tag } = encodedMessage;
				const isOwn = sender === userId;
				const botId = isOwn ? recipient : sender;
				const oldMessageId = data.meta?.regeneration?.oldMessageId;
				const isSexting =
					data.meta?.ranking?.labels?.includes('sexting') || false;
				const notOwnSexting = isSexting && !isOwn;

				// suggested text logic
				if (isNewMessage && !isOwn) {
					for (const { originalArgs } of dialogsApi.util.selectInvalidatedBy(
						listenerApi.getState() as RootState,
						[apiTags.suggestedTexts]
					) as { originalArgs: { userId: string; recipientId: string } }[]) {
						if (originalArgs.recipientId === botId) {
							listenerApi.dispatch(
								dialogsApi.util.updateQueryData(
									'getBotSuggestedText',
									originalArgs,
									(draft) => {
										draft.messageId = data.id;
										draft.suggestions = null;
									}
								) as AppDispatch
							);

							setTimeout(() => {
								listenerApi.dispatch(
									dialogsApi.util.updateQueryData(
										'getBotSuggestedText',
										{
											userId,
											recipientId: botId,
										},
										(draft) => {
											if (draft.messageId === data.id && !draft.suggestions) {
												draft.suggestions = [];
											}
										}
									) as AppDispatch
								);
							}, SUGGESTIONS_RESPONSE_TIMEOUT_MS);
						}
					}
				}

				if (notOwnSexting) {
					GTMAnalytics.userDidReceiveSextingMessage(botId);
				}

				if (!isOwn) {
					listenerApi.dispatch(messageReceived(botId));
				}

				listenerApi.dispatch(
					dialogsApi.util.updateQueryData(
						'getMessages',
						{ id: userId, recipientId: botId, omit: 0 },
						(draft) => {
							const existingMsg = draft?.messages
								? messagesSelector.selectById(draft.messages, data.tag)
								: undefined;
							const replacedMessage = draft?.messages
								? Object.values(
										messagesSelector.selectEntities(draft.messages) ?? {}
									).find(
										(message): message is TMessage =>
											message !== undefined && message.id === oldMessageId
									)
								: undefined;
							const newData = adaptVideoMessage(
								adaptSextingMessage(
									formatIMGPaths(addGifPath(adaptAudioMessage(data)))
								)
							);
							const currMessageId = existingMsg?.tag ?? replacedMessage?.tag;

							if (currMessageId && draft?.messages) {
								messagesAdapter.updateOne(draft.messages, {
									id: currMessageId,
									changes: newData,
								});
							} else if (isNewMessage && draft?.messages) {
								messagesAdapter.upsertOne(draft.messages, newData);
								if (typeof draft.totalLength === 'number') {
									draft.totalLength += 1;
								}
							}
						}
					) as AppDispatch
				);

				if (isNewMessage) {
					void updateChat(listenerApi, encodedMessage);
				} else {
					listenerApi.dispatch(
						chatVideoGenerationActions.deleteMessageIdByTag(tag)
					);
				}
			}
			if (isMsgType(msg, EventType.ChatMediaGenerationStarted)) {
				const { tag } = msg.payload;

				listenerApi.dispatch(
					chatVideoGenerationActions.attachTagToLastMessageId(tag)
				);
			}

			if (
				isMsgType(
					msg,
					EventType.ChatBecomeAvailable,
					EventType.ChatBecomeUnavailable
				)
			) {
				const {
					auth: {
						info: { id },
					},
					api,
				} = listenerApi.getState() as RootState;

				const botId = msg.payload.botId;

				void listenerApi.dispatch(
					generationApi.util.invalidateTags([
						apiTags.imagesGenerationBots,
						apiTags.videosGenerationBots,
					])
				);

				const hasMessages =
					api.queries[`getMessages-${msg.payload.botId}-${id}`];
				void listenerApi.dispatch(
					dialogsApi.endpoints.getMessages.initiate(
						{
							id,
							recipientId: botId,
							omit: 0,
						},
						{
							forceRefetch: !hasMessages, // with forceRefetch will create new cache instance (case when new resident opens by reaching level). But if we have instance already we dont need this (chat with DD)
						}
					)
				);
				void listenerApi.dispatch(
					botsApi.endpoints.getNextBots.initiate(
						{
							userId: id,
						},
						{
							forceRefetch: 5,
						}
					)
				);
				void listenerApi.dispatch(
					botsApi.endpoints.getChatsCount.initiate(
						{
							userId: id,
						},
						{
							forceRefetch: true,
						}
					)
				);
			}

			if (isMsgType(msg, EventType.SuggestedTextsGenerated)) {
				listenerApi.dispatch(
					dialogsApi.util.updateQueryData(
						'getBotSuggestedText',
						{
							userId,
							recipientId: msg.payload.botId,
						},
						(draft) => {
							draft.messageId = msg.payload.messageId;
							draft.suggestions = msg.payload.suggestions;
						}
					) as AppDispatch
				);
			}

			const isImageGenerated = isMsgType(msg, EventType.ImageGenerated);
			const isVideoGenerated = isMsgType(msg, EventType.VideoGenerated);
			const isImageUpdated = isMsgType(msg, EventType.ImageUpdated);
			const isVideoUpdated = isMsgType(msg, EventType.VideoUpdated);

			if (
				isImageGenerated ||
				isVideoGenerated ||
				isImageUpdated ||
				isVideoUpdated
			) {
				const isError =
					msg.payload.generated.status !== 'processing' &&
					msg.payload.generated.status !== 'generated' &&
					msg.payload.generated.status !== 'queued';

				if (
					isVideoGenerated &&
					isError &&
					msg.payload.generated.source === 'chat'
				) {
					return;
				}

				// Messages from mediagen miss source field
				if (msg.payload.generated.source === 'chat') {
					return;
				}

				const {
					api,
					auth: {
						info: { id },
					},
				} = listenerApi.getState() as RootState;

				const isQueryExists = api.queries[`getGeneratedMedia-${id}`];

				if (isImageGenerated) {
					ClientLogger.info(
						LoggerServices.NetworkService,
						LoggerMessages.ImageGeneratedEventReceived,
						{
							payload: {
								userId: id,
								botId: msg.payload?.generated?.botId,
								generationId: msg.payload?.generated?.id,
								queryExists: isQueryExists,
							},
						}
					);
				}

				if (!isQueryExists) {
					return;
				}

				void listenerApi.dispatch(
					generationApi.util.updateQueryData(
						'getGeneratedMedia',
						{ userId: id, omit: NaN, select: NaN },
						(draft) => {
							const current = draft.media?.find(
								(it) => it.id === msg.payload.generated.id
							);
							if (msg.payload.generated.status === 'generated') {
								if (isImageGenerated) {
									saveToSessionStorage(ssKeys.IMG_GEN_PREV_SOME_SUCCESS, true);
								} else if (isVideoGenerated) {
									saveToSessionStorage(
										ssKeys.VIDEO_GEN_PREV_SOME_SUCCESS,
										true
									);
								}
							}

							const mediaItem = prepareMediaItem(msg.payload.generated);

							if (current) {
								Object.assign(current, mediaItem);
							} else {
								draft.media?.unshift(mediaItem);
								if (typeof draft.totalLength === 'number') {
									draft.totalLength += 1;
								}
							}
						}
					) as AppDispatch
				);
				const { type } = TokenManager.getInfo();

				if (
					type === 'demo' &&
					loadFromSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, false) &&
					isImageGenerated
				) {
					GTMAnalytics.createFreeImage(!isError);
				}
				if (isError) {
					listenerApi.dispatch(
						generationApi.util.invalidateTags([
							isImageGenerated
								? apiTags.imagesGenerationQuota
								: apiTags.videosGenerationQuota,
						])
					);
					if (isImageGenerated) {
						Toaster.showError({
							titleKey: 'imageGenerationFailedTitle',
							textKey: 'imageGenerationFailedMessage',
						});
					} else {
						Toaster.showError({
							titleKey: 'videoGenerationFailedTitle',
							textKey: 'videoGenerationFailedMessage',
						});
					}
				} else if (isVideoGenerated) {
					Toaster.showSuccess({
						titleKey: 'videoGenerationSuccessTitle',
						textKey: 'videoGenerationSuccessMessage',
					});
				}
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, false);
			}

			const isBlocked = isMsgType(msg, EventType.ChatBotBlocked);
			const isUnblocked = isMsgType(msg, EventType.ChatBotUnblocked);

			if (isBlocked || isUnblocked) {
				const { userId, botIds } = msg.payload;

				botIds.forEach((botId) =>
					toggleChatBotBlock(userId, botId, isBlocked, listenerApi)
				);
			}
		});
	},
});

const toggleChatBotBlock = (
	userId: string,
	botId: string,
	blocked: boolean,
	listenerApi: ListenerEffectAPI<
		unknown,
		ThunkDispatch<unknown, unknown, AnyAction>,
		unknown
	>
) => {
	chatEntity.update(listenerApi.getState, listenerApi.dispatch, (draft) => {
		if (draft.botId === botId) {
			draft.blocked = blocked;
		}
	});
};
